import React, {useCallback, useEffect, useMemo, useState} from 'react';
import api from '../api/api';
import {useLookups} from '../context/LookupContext';
import { applicationsData } from './Applications';

export default function AddTaskModal({ isOpen, onClose, onTaskCreated, initialDate, initialTaskData }) {
  const { lookups, loading: lookupsLoading } = useLookups();
  const fallbackFocusAreaOptions = useMemo(
    () => [
      { id: '2', name: 'Job Performance' },
      { id: '3', name: 'Skilling' },
      { id: '4', name: 'Giving Back' },
      { id: '5', name: 'Networking' },
      { id: '6', name: 'Feedback' }
    ],
    []
  );

  const focusAreaOptions = useMemo(() => {
    const rawFocusAreas = lookups?.focusAreas ?? lookups?.focusAreaID;

    if (Array.isArray(rawFocusAreas) && rawFocusAreas.length > 0) {
      const normalizedFromArray = rawFocusAreas
        .map((area, index) => {
          if (typeof area === 'string') {
            return { id: String(index + 1), name: area };
          }

          if (!area || typeof area !== 'object') {
            return null;
          }

          const id = area.id ?? area.FocusAreaID ?? index + 1;
          const name = area.name ?? area.FocusAreaName;

          if (!name) {
            return null;
          }

          return {
            id: String(id),
            name: String(name)
          };
        })
        .filter(Boolean);

      if (normalizedFromArray.length > 0) {
        return normalizedFromArray;
      }
    }

    if (rawFocusAreas && typeof rawFocusAreas === 'object') {
      const fromObject = Object.entries(rawFocusAreas)
        .map(([key, value], index) => {
          if (value && typeof value === 'object') {
            const id = value.id ?? value.FocusAreaID ?? index + 1;
            const name = value.name ?? value.FocusAreaName ?? key;
            return { id: String(id), name: String(name) };
          }

          const numericValue = Number.parseInt(value, 10);
          const id = Number.isNaN(numericValue) ? String(index + 1) : String(numericValue);
          return { id, name: String(key) };
        })
        .filter((area) => Boolean(area.name));

      if (fromObject.length > 0) {
        return fromObject;
      }
    }

    return fallbackFocusAreaOptions;
  }, [lookups, fallbackFocusAreaOptions]);

  const [formData, setFormData] = useState({
    Title: '',
    start: '',
    end: '',
    allDay: false,
    FocusAreaID: '',
    isOnCalendar: 1,
    CalendarEventID: null,
    ApplicationID: ''
  });

  const [isApplication, setIsApplication] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const resolveFocusAreaId = useCallback(
    (focusArea) => {
      if (!focusArea || focusAreaOptions.length === 0) {
        return '';
      }

      const target = String(focusArea).trim().toLowerCase();
      const matched = focusAreaOptions.find((option) => {
        const optionId = String(option?.id ?? '').trim().toLowerCase();
        const optionName = String(option?.name ?? '').trim().toLowerCase();
        return optionId === target || optionName === target;
      });

      return matched?.id ? String(matched.id) : '';
    },
    [focusAreaOptions]
  );

  useEffect(() => {
    if (!lookupsLoading && focusAreaOptions.length > 0 && !formData.FocusAreaID) {
      const firstFocusAreaId =
        focusAreaOptions[0]?.id ??
        focusAreaOptions[0]?.FocusAreaID ??
        '';
      if (firstFocusAreaId) {
        setFormData(prev => ({ ...prev, FocusAreaID: String(firstFocusAreaId) }));
      }
    }
  }, [lookupsLoading, focusAreaOptions, formData.FocusAreaID]);

  // Reset and prefill values whenever the modal is opened.
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const defaultFocusAreaId =
      focusAreaOptions[0]?.id ??
      focusAreaOptions[0]?.FocusAreaID ??
      '';

    let prefilledStart = '';
    if (initialDate) {
      const date = new Date(initialDate);
      prefilledStart = date.toISOString().slice(0, 16);
    }

    const focusAreaFromTask =
      initialTaskData?.FocusAreaID ||
      initialTaskData?.focusAreaId ||
      initialTaskData?.FocusArea ||
      initialTaskData?.focusArea ||
      '';

    setFormData({
      Title: initialTaskData?.Title || initialTaskData?.task || '',
      start: prefilledStart,
      end: '',
      allDay: false,
      FocusAreaID: resolveFocusAreaId(focusAreaFromTask) || String(defaultFocusAreaId || ''),
      isOnCalendar: 1,
      CalendarEventID: null,
      ApplicationID: ''
    });
  }, [isOpen, initialDate, initialTaskData, focusAreaOptions, resolveFocusAreaId]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const parsedFocusAreaID = Number.parseInt(formData.FocusAreaID, 10);
      if (Number.isNaN(parsedFocusAreaID)) {
        setError('Please select a valid focus area.');
        setIsSubmitting(false);
        return;
      }

      const response = await api.post('/task/create', {
        ...formData,
        FocusAreaID: parsedFocusAreaID,
        end: formData.end || formData.start
      });

      if (onTaskCreated) {
        await Promise.resolve(onTaskCreated(response.data));
      }
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create task');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-[1000] p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full mt-16 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-black">Create New Task</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>}

        <div>
          <label className="block text-sm font-medium text-black mb-2">Task Title *</label>
          <input type="text" name="Title" value={formData.Title} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-black" placeholder="Enter task title" required />
        </div>

        <div>
          <label className="block text-sm font-medium text-black mb-2">Focus Area</label>
          <select
            name="FocusAreaID"
            value={formData.FocusAreaID}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-black bg-white"
          > 
            {focusAreaOptions.map((area, index) => {
              const optionId = area?.id ?? area?.FocusAreaID ?? String(index + 1);
              const optionName = area?.name ?? area?.FocusAreaName ?? `Focus Area ${optionId}`;

              return (
                <option key={optionId} value={String(optionId)}>
                  {String(optionName)}
                </option>
              );
            })}
          </select>
        </div>

        <div className="flex items-center">
          <input type="checkbox" name="allDay" checked={formData.allDay} onChange={handleInputChange} className="h-4 w-4 text-primary-600 border-gray-300 rounded" />
          <label className="ml-2 block text-sm text-black">All day event</label>
        </div>

        <div className="flex items-center gap-4">
          <div className="w-[48%]">
            <label className="block text-sm font-medium text-black mb-2">
              Start {formData.allDay ? 'Date' : 'Date & Time'} <span className="text-red-600">*</span>
            </label>
            <input type={formData.allDay ? 'date' : 'datetime-local'} name="start" value={formData.start} 
                   onChange={handleInputChange} 
                   className="w-full px-3 py-2 border border-gray-300 rounded-lg text-black" required />
          </div>
          {!formData.allDay && (
            <div className="w-[48%]">
              <label className="block text-sm font-medium text-black mb-2">End Date & Time <span className="text-xs text-slate-500">(Optional)</span></label>
              <input type="datetime-local" name="end" value={formData.end} 
                     onChange={handleInputChange} 
                     className="w-full px-3 py-2 border border-gray-300 rounded-lg text-black" />
            </div>
          )}
        </div>

        <div className="flex gap-3 pt-4">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2 text-black bg-gray-100 rounded-lg">Cancel</button>
          <button type="submit" disabled={isSubmitting} className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50">
              {isSubmitting ? 'Creating...' : 'Create Task'}
          </button>
        </div>
      </form>
    </div>
  </div>
);}